import type { FC, ReactNode } from 'react'
import { createContext, useState, useMemo, useContext, useEffect } from 'react'

import type { Locale, WhiteLabel } from '@skytech/types'
import { DEFAULT_LOCALE, getWhiteLabelFromParams, isLocale } from '@skytech/utils'
import { useLocation } from 'react-router-dom'

type AppContext = {
  locale: Locale
  setLocale: (v: Locale) => void
  isWizardMode: boolean
  setIsWizardMode: (v: boolean) => void
  whiteLabel: WhiteLabel
  setWhiteLabel: (v: WhiteLabel) => void
  isEditTranslationsMode: boolean
  setIsEditTranslationsMode: (v: boolean) => void
}

const Context = createContext<AppContext>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  isWizardMode: false,
  setIsWizardMode: () => {},
  whiteLabel: 'skytech',
  setWhiteLabel: () => {},
  isEditTranslationsMode: false,
  setIsEditTranslationsMode: () => {},
})

export const AppContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation()

  // todo: adjust to the other apps standard
  let browserLocale = navigator.language.split(/[-_]/)[0]
  // To have all Norwegian translations under one key
  if (browserLocale === 'nb') browserLocale = 'no'

  const storedLocale = getStoredLocale()
  const defaultLocale = storedLocale || (browserLocale as Locale) || DEFAULT_LOCALE
  const [locale, setLocale] = useState<Locale>(defaultLocale)
  const [isWizardMode, setIsWizardMode] = useState(false)
  const [whiteLabel, setWhiteLabel] = useState<WhiteLabel>('skytech')
  const [isEditTranslationsMode, setIsEditTranslationsMode] = useState(false)

  const handleSetWhiteLabel = (_whiteLabel: WhiteLabel) => {
    setWhiteLabel(_whiteLabel)
    // todo: remove when the organisation is specified in the url
    localStorage.setItem('org', _whiteLabel)
    // todo: find a way to keep the org info only in one place
    document.getElementsByTagName('html')[0].className = _whiteLabel
  }

  const handleSetLocale = (_locale: Locale) => {
    setLocale(_locale)
    // To preselect locale on the login page
    localStorage.setItem('locale', _locale)
  }

  const value: AppContext = useMemo(
    () => ({
      locale,
      isWizardMode,
      whiteLabel,
      isEditTranslationsMode,
      setWhiteLabel: handleSetWhiteLabel,
      setLocale: handleSetLocale,
      setIsWizardMode,
      setIsEditTranslationsMode,
    }),
    [locale, isWizardMode, whiteLabel, isEditTranslationsMode]
  )

  useEffect(() => {
    getWhiteLabelFromParams({ location, setWhiteLabel: handleSetWhiteLabel })
  }, [location])

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const useAppContext = () => useContext(Context)

function getStoredLocale() {
  const storedLocale = localStorage.getItem('locale')
  if (!storedLocale) return null

  if (isLocale(storedLocale)) return storedLocale as Locale

  return null
}
